import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReviews } from '../../context/ReviewContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getConnections } from '../../services/connectionsService';
import { Star, ThumbsUp, Flag, Trash2, MessageSquare, Share2 } from 'lucide-react';

const ProductReviews = ({ productId, productName }) => {
  const { 
    getProductReviews, 
    getProductRating, 
    hasUserReviewed, 
    addReview, 
    deleteReview,
    markHelpful,
    reportReview,
    addEmojiReaction,
    getReviewReactions,
    getUserReactions
  } = useReviews();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [friendUserIds, setFriendUserIds] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [connectionsError, setConnectionsError] = useState(false);

  useEffect(() => {
    getConnections()
      .then((ids) => {
        setFriendUserIds(ids);
        setConnectionsError(false);
      })
      .catch(() => {
        setFriendUserIds([]);
        setConnectionsError(true);
      });
  }, []);

  const productReviews = getProductReviews ? getProductReviews(productId, friendUserIds) : [];
  const productRating = getProductRating ? getProductRating(productId) : { average: 0, count: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } };
  const userHasReviewed = hasUserReviewed ? hasUserReviewed(productId) : false;

  const handleShareProduct = async () => {
    setSharing(true);
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const text = `Check out ${productName} on Rainbow Sails!`;
    try {
      if (navigator.share) {
        await navigator.share({ title: productName, text, url });
        showToast('Shared with friends!', 'success');
      } else {
        await navigator.clipboard.writeText(url);
        showToast('Link copied! Share with your friends.', 'success');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(url).catch(() => {});
        showToast('Link copied! Share with your friends.', 'success');
      }
    } finally {
      setSharing(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      showToast('Please sign in to leave a review', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await addReview({
        productId,
        productName,
        ...reviewForm
      });
      
      setReviewForm({ rating: 5, title: '', comment: '' });
      setShowReviewForm(false);
      showToast('Review submitted successfully!', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to submit review', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await deleteReview(reviewId);
        showToast('Review deleted successfully', 'success');
      } catch (error) {
        showToast(error.message || 'Failed to delete review', 'error');
      }
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    try {
      await markHelpful(reviewId);
    } catch (error) {
      showToast(error.message || 'Failed to update', 'error');
    }
  };

  const handleReportReview = async (reviewId) => {
    const reason = prompt('Why are you reporting this review?');
    if (reason) {
      try {
        await reportReview(reviewId, reason);
        showToast('Review reported. Thank you for helping keep our community safe.', 'success');
      } catch (error) {
        showToast(error.message || 'Failed to report', 'error');
      }
    }
  };

  const handleEmojiReaction = async (reviewId, emoji) => {
    try {
      await addEmojiReaction(reviewId, emoji);
    } catch (error) {
      console.error('Failed to add emoji reaction:', error);
    }
  };

  const renderStars = (rating, size = 20, interactive = false, onRatingChange = null) => {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={size}
            style={{
              color: star <= rating ? '#f59e0b' : '#374151',
              fill: star <= rating ? '#f59e0b' : 'transparent',
              cursor: interactive ? 'pointer' : 'default',
              transition: 'all 0.2s ease'
            }}
            onClick={interactive ? () => onRatingChange(star) : undefined}
          />
        ))}
      </div>
    );
  };

  const hasReviews = productRating.count > 0 || productReviews.length > 0;
  
  return (
    <div style={{
      background: 'rgba(31, 41, 55, 0.8)',
      backdropFilter: 'blur(4px)',
      borderRadius: '12px',
      padding: hasReviews ? '32px' : '16px',
      border: '2px solid #8b5cf6',
      marginTop: '32px'
    }}>
      {/* Reviews Header */}
      <div style={{ marginBottom: hasReviews ? '32px' : '16px' }}>
        {connectionsError && user && (
          <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 8px 0' }}>
            Couldn&apos;t load friends list – reviews shown in default order.
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: hasReviews ? '16px' : '8px' }}>
          <h2 style={{ fontSize: hasReviews ? '2rem' : '1.25rem', fontWeight: 'bold', color: '#8b5cf6', margin: 0 }}>
            ⭐ Reviews & Ratings
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={handleShareProduct}
              disabled={sharing}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: '12px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: sharing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              title="Share with friends"
            >
              <Share2 size={16} />
              {sharing ? 'Sharing...' : 'Share'}
            </button>
            {user && !userHasReviewed && (
              <button
                onClick={() => setShowReviewForm(!showReviewForm)}
                style={{
                  background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <MessageSquare size={16} />
                Write Review
              </button>
            )}
          </div>
        </div>

        {/* Rating Summary */}
        {productRating.count > 0 ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '20px',
            borderRadius: '12px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#f59e0b' }}>
                {productRating.average}
              </div>
              <div style={{ marginBottom: '8px' }}>
                {renderStars(Math.round(productRating.average), 24)}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '14px' }}>
                {productRating.count} review{productRating.count !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Rating Distribution */}
            <div style={{ flex: 1 }}>
              {[5, 4, 3, 2, 1].map(rating => (
                <div key={rating} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '4px'
                }}>
                  <span style={{ color: 'white', fontSize: '14px', width: '20px' }}>
                    {rating}★
                  </span>
                  <div style={{
                    flex: 1,
                    height: '8px',
                    background: '#374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${productRating.count > 0 ? (productRating.distribution[rating] / productRating.count) * 100 : 0}%`,
                      height: '100%',
                      background: '#f59e0b',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <span style={{ color: '#9ca3af', fontSize: '12px', width: '30px' }}>
                    {productRating.distribution[rating]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '16px',
            borderRadius: '12px'
          }}>
            <Star size={24} color="#9ca3af" style={{ margin: '0 auto 8px' }} />
            <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
              No reviews yet. Be the first to share your experience!
            </p>
          </div>
        )}
      </div>

      {/* Review Form */}
      <AnimatePresence>
        {showReviewForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid #8b5cf6',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px'
            }}
          >
            <h3 style={{ color: '#a78bfa', fontSize: '1.25rem', marginBottom: '20px' }}>
              ✨ Share Your Experience
            </h3>
            
            <form onSubmit={handleSubmitReview}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Rating
                </label>
                {renderStars(reviewForm.rating, 32, true, (rating) => 
                  setReviewForm({ ...reviewForm, rating })
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Review Title
                </label>
                <input
                  type="text"
                  value={reviewForm.title}
                  onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                  placeholder="Sum up your experience..."
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(0, 0, 0, 0.3)',
                    color: 'white',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Your Review
                </label>
                <textarea
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                  placeholder="Tell others about your experience with this consciousness-expanding art..."
                  required
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(0, 0, 0, 0.3)',
                    color: 'white',
                    fontSize: '16px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: submitting ? '#6b7280' : 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? '⏳ Submitting...' : '✨ Submit Review'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReviewForm(false)}
                  style={{
                    background: 'transparent',
                    color: '#9ca3af',
                    border: '1px solid #374151',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reviews List */}
      <div>
        <h3 style={{ color: 'white', fontSize: hasReviews ? '1.25rem' : '1rem', marginBottom: hasReviews ? '20px' : '12px' }}>
          Customer Reviews ({productReviews.length})
        </h3>
        
        {productReviews.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '16px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '12px'
          }}>
            <MessageSquare size={20} color="#9ca3af" style={{ margin: '0 auto 8px' }} />
            <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
              No reviews yet. {user ? 'Be the first to review!' : 'Sign in to leave a review.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {productReviews.map((review, index) => {
              // Calculate if this is the most reacted review
              const allReactions = productReviews.map(r => 
                Object.values(r.reactions || {}).reduce((total, userReactions) => {
                  return total + userReactions.length;
                }, 0)
              );
              const maxReactions = Math.max(...allReactions);
              const totalReactions = Object.values(review.reactions || {}).reduce((total, userReactions) => {
                return total + userReactions.length;
              }, 0);
              const isMostReacted = totalReactions > 0 && totalReactions === maxReactions;
              
              return (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    background: isMostReacted ? 'rgba(139, 92, 246, 0.1)' : 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '12px',
                    padding: '20px',
                    border: isMostReacted ? '2px solid rgba(139, 92, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                    position: 'relative'
                  }}
                >
                  {/* Top review indicator */}
                  {index === 0 && isMostReacted && (
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      left: '20px',
                      background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      🏆 TOP REVIEW
                    </div>
                  )}
                {/* Review Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {renderStars(review.rating, 16)}
                        <span style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '14px' }}>
                          {review.rating}/5
                        </span>
                      </div>
                      {review.verified && (
                        <span style={{
                          background: '#10b981',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          ✓ VERIFIED
                        </span>
                      )}
                      {(() => {
                        const totalReactions = Object.values(review.reactions || {}).reduce((total, userReactions) => {
                          return total + userReactions.length;
                        }, 0);
                        
                        // Show "Most Reacted" badge if this review has the most reactions
                        const allReactions = productReviews.map(r => 
                          Object.values(r.reactions || {}).reduce((total, userReactions) => {
                            return total + userReactions.length;
                          }, 0)
                        );
                        const maxReactions = Math.max(...allReactions);
                        const isMostReacted = totalReactions > 0 && totalReactions === maxReactions;
                        
                        if (isMostReacted && totalReactions > 0) {
                          return (
                            <span style={{
                              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              🔥 MOST REACTED ({totalReactions})
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <h4 style={{ color: 'white', fontSize: '16px', fontWeight: 'bold', margin: 0 }}>
                      {review.title}
                    </h4>
                    <p style={{ color: '#9ca3af', fontSize: '12px', margin: '4px 0 0 0' }}>
                      By {review.userName} • {new Date(review.createdAt).toLocaleDateString('en-AU', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  
                  {/* Review Actions */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {user && user.id === review.userId && (
                      <button
                        onClick={() => handleDeleteReview(review.id)}
                        style={{
                          background: 'transparent',
                          color: '#ef4444',
                          border: 'none',
                          padding: '4px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        title="Delete review"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    {user && user.id !== review.userId && (
                      <button
                        onClick={() => handleReportReview(review.id)}
                        style={{
                          background: 'transparent',
                          color: '#9ca3af',
                          border: 'none',
                          padding: '4px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        title="Report review"
                      >
                        <Flag size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Review Content */}
                <p style={{
                  color: '#e5e7eb',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  marginBottom: '16px',
                  margin: 0
                }}>
                  {review.comment}
                </p>

                {/* Review Footer */}
                <div style={{ marginTop: '16px' }}>
                  {/* Emoji Reactions */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {/* Available Emoji Reactions */}
                      {['👍', '❤️', '😂', '😮', '😢', '😡'].map((emoji) => {
                        const reactions = getReviewReactions(review.id);
                        const count = reactions[emoji] || 0;
                        const userReactions = getUserReactions(review.id);
                        const isUserReacted = userReactions.includes(emoji);
                        
                        return (
                          <button
                            key={emoji}
                            onClick={() => handleEmojiReaction(review.id, emoji)}
                            disabled={!user || user.id === review.userId}
                            style={{
                              background: isUserReacted ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                              border: isUserReacted ? '1px solid #8b5cf6' : '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '16px',
                              padding: '4px 8px',
                              fontSize: '14px',
                              cursor: user && user.id !== review.userId ? 'pointer' : 'default',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              opacity: user && user.id !== review.userId ? 1 : 0.6
                            }}
                            onMouseEnter={(e) => {
                              if (user && user.id !== review.userId) {
                                e.target.style.transform = 'scale(1.1)';
                                e.target.style.background = 'rgba(139, 92, 246, 0.3)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (user && user.id !== review.userId) {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.background = isUserReacted ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)';
                              }
                            }}
                            title={user && user.id === review.userId ? "Can't react to your own review" : `React with ${emoji}`}
                          >
                            <span>{emoji}</span>
                            {count > 0 && (
                              <span style={{ 
                                color: isUserReacted ? '#8b5cf6' : '#9ca3af',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}>
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Helpful Button and Edit Info */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button
                      onClick={() => handleMarkHelpful(review.id)}
                      disabled={!user || user.id === review.userId}
                      style={{
                        background: 'transparent',
                        color: '#9ca3af',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: user && user.id !== review.userId ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'color 0.3s ease',
                        opacity: user && user.id !== review.userId ? 1 : 0.6
                      }}
                      onMouseEnter={(e) => {
                        if (user && user.id !== review.userId) {
                          e.target.style.color = '#10b981';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (user && user.id !== review.userId) {
                          e.target.style.color = '#9ca3af';
                        }
                      }}
                      title={user && user.id === review.userId ? "Can't mark your own review as helpful" : "Mark as helpful"}
                    >
                      <ThumbsUp size={12} />
                      Helpful ({review.helpful || 0})
                    </button>
                    
                    {review.updatedAt !== review.createdAt && (
                      <span style={{ color: '#6b7280', fontSize: '10px', fontStyle: 'italic' }}>
                        Edited {new Date(review.updatedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* User Review Status */}
      {user && userHasReviewed && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid #10b981',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '20px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#6ee7b7', fontSize: '14px', margin: 0 }}>
            ✅ Thank you for your review! You've already reviewed this product.
          </p>
        </div>
      )}

      {!user && (
        <div style={{
          background: 'rgba(139, 92, 246, 0.1)',
          border: '1px solid #8b5cf6',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '20px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#a78bfa', fontSize: '14px', margin: '0 0 12px 0' }}>
            Want to leave a review? Sign in to share your experience!
          </p>
          <button
            onClick={() => window.location.href = '/auth'}
            style={{
              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Sign In to Review
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
